/**
 * How a per-specialist column should RENDER — as a pure function, so the rule
 * survives being copied into six more surfaces.
 *
 * The API models every column as `{status:"known", …}` | `{status:"unknown",
 * reason}`. The temptation in a table cell is `col.status === "known" ? value :
 * "—"`, which throws away the one thing the server went out of its way to send:
 * WHY it does not know. An em-dash reads as "empty"; the truth is "not reported
 * yet", "home is broken", or "no manifest" — three different operator actions.
 *
 * This is the same distinction the Agent Bus alias table had to grow the hard
 * way (`bus-section-state.ts`): could-not-read is not empty. Here the server
 * hands it over for free and the only way to lose it is in the cell.
 */

export type ColumnRender =
  /** Show the payload. */
  | { kind: "value" }
  /** Show an unknown affordance carrying the server's own reason. */
  | { kind: "unknown"; reason: string };

export function renderColumn(col: { status: string; reason?: string } | null | undefined): ColumnRender {
  if (!col) {
    // Defensive: a column the server did not send at all is still not "empty".
    return { kind: "unknown", reason: "the server did not return this column" };
  }
  if (col.status === "known") return { kind: "value" };
  return {
    kind: "unknown",
    // Never fabricate a reason, but never render a bare dash either.
    reason: col.reason?.trim() || "reported as unknown, without a stated reason",
  };
}

/** True when the roster view is incomplete. Reads the SERVER's verdict rather
 *  than re-deriving it by scanning rows — a client that computes its own health
 *  will eventually disagree with the server about it, and then the operator has
 *  two answers and no tiebreak. */
export function isDegraded(result: { degraded?: boolean } | null | undefined): boolean {
  return result?.degraded === true;
}
